import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  styled,
  Typography,
} from '@mui/material';
import { ReactNode, useCallback, useEffect, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { queryPeerProfileInfo } from '../service/PeerProfileService';
import { PeerProfile } from '../model/PeerProfile';
import {
  LoadingIndicator,
  displayFullName,
  hasName,
} from '@xolv-mybrightlink/common';
import { FormattedMessage } from 'react-intl';
import { PatientProfile } from '../../profile/component/PatientProfile';
import { formattedAddress } from './ConnectionItem';

const BootstrapDialog = styled(Dialog)(() => ({
  '& .MuiDialogContent-root': {
    padding: 0,
  },
  '& .MuiBackdrop-root': {
    backgroundColor: 'rgba(59, 69, 89, 0.3)',
  },
}));

type ProfileInfoDialogProps = {
  openDialog: boolean;
  onCloseDialog: () => void;
  peerProfileGuid: string;
  actionElement: ReactNode;
};

export const ProfileInfoDialog = ({
  openDialog,
  onCloseDialog,
  peerProfileGuid,
  actionElement,
}: ProfileInfoDialogProps) => {
  const [profile, setProfile] = useState<PeerProfile | null>(null);

  useEffect(() => {
    const fetchProfileInfo = async () => {
      const peerProfileInfo = await queryPeerProfileInfo(peerProfileGuid);
      setProfile(peerProfileInfo);
    };
    fetchProfileInfo();
  }, [peerProfileGuid]);

  const handleClose = useCallback(() => {
    onCloseDialog();
  }, [onCloseDialog]);

  return (
    <BootstrapDialog
      onClose={handleClose}
      aria-labelledby='customized-dialog-title'
      open={openDialog}
      PaperProps={{
        sx: {
          px: 10,
          py: 5,
          borderRadius: 3,
          overflow: 'visible',
          minHeight: 0.8,
          minWidth: 0.7,
          justifyContent: 'center',
        },
      }}
    >
      {!profile ? (
        <LoadingIndicator />
      ) : (
        <>
          <DialogTitle component='div' sx={{ m: 0, p: 0, pb: 5 }}>
            <Box>
              <Typography variant='body2' color='#626a7a' textAlign='center'>
                <FormattedMessage id='community.peers.item.address' />
                {hasName(profile) && ` ${formattedAddress(profile)}`}
              </Typography>
              <Box sx={{ position: 'relative' }}>
                <Typography
                  variant='subtitle1'
                  color='text.main'
                  fontWeight={500}
                  textAlign='center'
                >
                  {hasName(profile)
                    ? displayFullName({
                        firstName: profile.firstName,
                        lastName: profile.lastName,
                      })
                    : formattedAddress(profile)}
                </Typography>
                <Box
                  sx={{
                    position: 'absolute',
                    top: -4,
                    right: 0,
                    backgroundColor: '#fff',
                  }}
                >
                  {actionElement}
                </Box>
              </Box>
            </Box>
            <IconButton
              aria-label='close'
              onClick={handleClose}
              sx={{
                position: 'absolute',
                right: 0,
                top: -50,
                color: 'primary.contrastText',
                border: '1px solid white',
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ py: 5, width: 'calc(100% + 24px)' }}>
            <Box sx={{ display: 'flex', gap: 4, overflowX: 'hidden' }}>
              <Box sx={{ width: 0.3, flexGrow: 0, flexShrink: 0 }}>
                <Typography
                  color='primary.main'
                  sx={{ mb: 5, fontWeight: 500 }}
                >
                  <FormattedMessage id='profile.connect.profile.summary' />
                </Typography>
                <Typography sx={{ mb: 5 }}>{profile.biography}</Typography>
                <Typography
                  color='primary.main'
                  sx={{ mb: 5, fontWeight: 500 }}
                >
                  <FormattedMessage id='profile.client.interest' />
                </Typography>
                {(profile.userInterests ?? []).map((interest) => (
                  <Typography key={interest} sx={{ mb: 1 }}>
                    <FormattedMessage id={`profile.interests.${interest}`} />
                  </Typography>
                ))}
              </Box>
              <Box sx={{ pl: 3, pr: 6, pb: 4, width: 0.7 }}>
                <Typography
                  color='primary.main'
                  sx={{ mb: 5, fontWeight: 500, px: 3 }}
                >
                  <FormattedMessage
                    id='profile.connect.children.summary'
                    values={{ numChild: profile.patients.length }}
                  />
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 10,
                    pl: 3,
                    pb: 10,
                  }}
                >
                  {(profile.patients ?? []).map((patient) => (
                    <Box key={patient.id}>
                      <PatientProfile key={patient.id} patient={patient} />
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          </DialogContent>
        </>
      )}
    </BootstrapDialog>
  );
};
